import { DetailsRow, getTheme, IColumn, IDetailsListProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPCheckbox,
  IPFontIcon,
  IPLink,
  IPScrollablePane,
  IPShimmeredDetailsList,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import { IPeriodFilterData, PeriodFilterDataOption } from 'components/Filter/PeriodFilter';
import { getPeriodURLParams } from 'components/Filter/PeriodFilterHelper';
import HrefLink from 'components/Links/HrefLink';
import TextInfo from 'components/Notification/Info';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalAppName } from 'globalConstants';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { eventIcon, formIcon, globalStackTokensGapSmall, taskIcon } from 'globalStyles';
import Task, { TaskTypes } from 'models/tasks/task';
import TaskTemplateScene from 'models/tasks/taskTemplateScene';
import TaskType from 'models/tasks/taskType';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { toLocaleDateShort } from 'utils/datetime';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { sortOnString } from 'utils/sorting';
import { navigateToExternalUrl } from 'utils/url';

interface ITaskTemplateDetailsList {
  data: TaskTemplateScene;
  isBusy?: boolean;
  onEdit: (item: Task) => void;
  searchQuery?: string;
  updateTasks: (tasks: Task[]) => void;
  // one row selected at a time, for the commands on a template (PLAN-CTI-085); without it the rows cannot be selected
  selection?: IPSelection;
}

export const TaskTemplateDetailsList = (props: ITaskTemplateDetailsList) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation, tasks', 'forms', 'adminAuth']);
  const [columns, setColumns] = useState<IColumn[] | undefined>(undefined);
  const theme = getTheme();

  //need to update the state with useEffect on prop change because we use the prop data in the column render methods
  useEffect(() => {
    const getColumns = (): IColumn[] => {
      const allColumns: IColumn[] = [
        {
          key: 'name',
          name: t('tasks:TabTemplates.Columns.Name'),
          minWidth: 100,
          maxWidth: 500,
          isResizable: true,
          isRowHeader: true,
          onRender: (item?: Task, index?: number, column?: IColumn) => {
            if (!item) return;

            return (
              <IPText>
                <IPLink
                  onClick={() => {
                    props.onEdit(item);
                  }}
                >
                  {item.name}
                </IPLink>
              </IPText>
            );
          },
        },
        {
          key: 'type',
          name: t('tasks:TabTemplates.Columns.Type'),
          minWidth: 200,
          maxWidth: 400,
          isResizable: true,
          isCollapsible: true,
          isMultiline: true,
          onRender: (item?: Task, index?: number, column?: IColumn) => {
            if (!item) return;

            return (
              <IPStack>
                <IPStack
                  horizontal
                  tokens={globalStackTokensGapSmall}
                  styles={{ root: { fontSize: 16, color: theme.palette.themeSecondary } }}
                >
                  <IPFontIcon {...(item.taskType === TaskTypes.Template ? taskIcon : eventIcon)} />
                  <IPText>{item.getTypeText(t)}</IPText>
                </IPStack>
                {/* the audit procedure library is hidden, so it never reaches this list any more:
                    it is managed from the Audits scene and nowhere else */}
                {item.taskTypeId && (
                  <TextInfo
                    message={
                      item.systemTaskType > 0
                        ? t('tasks:TabTemplates.Columns.SystemFormInfo', {
                            type: TaskType.getSystemTypeText(item.systemTaskType, t),
                            appName: globalAppName,
                          })
                        : undefined
                    }
                  >
                    <IPStack
                      horizontal
                      tokens={globalStackTokensGapSmall}
                      styles={{
                        root: { fontSize: 16, color: item.systemTaskType > 0 ? 'green' : theme.palette.themeSecondary },
                      }}
                    >
                      <IPFontIcon {...formIcon} />
                      <IPText>
                        {t('tasks:TabTemplates.Columns.Form', {
                          form: item.formVersion?.name ?? appContext.globalDataCache.taskTypes.get(item?.taskTypeId).name,
                        })}
                      </IPText>
                    </IPStack>
                  </TextInfo>
                )}
              </IPStack>
            );
          },
        },
        {
          key: 'tags',
          name: t('tasks:TabTemplates.Columns.Tags'),
          minWidth: 150,
          maxWidth: 400,
          isResizable: true,
          isCollapsible: true,
          onRender: (item?: Task, index?: number, column?: IColumn) => {
            if (!item) return;

            return <KeyValueTags tags={appContext.globalDataCache.tags.getItemsForId(item?.tagIds)} />;
          },
        },
        {
          key: 'active',
          name: t('tasks:TabTemplates.Columns.Active'),
          minWidth: 100,
          maxWidth: 100,
          isResizable: true,
          isSorted: true,
          isSortedDescending: true,
          onRender: (item?: Task, index?: number, column?: IColumn) => {
            if (!item) return;

            return (
              <IPTooltipHost
                content={
                  item.systemTaskType > 0
                    ? t('tasks:TabTemplates.Columns.SystemFormInfo', {
                        type: TaskType.getSystemTypeText(item.systemTaskType, t),
                        appName: globalAppName,
                      })
                    : undefined
                }
              >
                <IPCheckbox disabled={item.systemTaskType > 0} checked={item.completed === undefined} />
              </IPTooltipHost>
            );
          },
        },
        {
          key: 'created-by',
          name: t('tasks:TabTemplates.Columns.CreatedBy'),
          minWidth: 150,
          isResizable: true,
          isCollapsible: true,
          onRender: (item?: Task, index?: number, column?: IColumn) => {
            if (!item) return;

            return (
              <IPPersona
                imageUrl={appContext.globalDataCache.users.getPhotoURLObj(item.creator?.id)}
                size={IPPersonaSize.size24}
                text={item.creator?.name}
              />
            );
          },
        },
        {
          key: 'used-count',
          name: t('tasks:TabTemplates.Columns.UsedCount'),
          minWidth: 150,
          isResizable: true,
          isCollapsible: true,
          onRender: (item?: Task, index?: number, column?: IColumn) => {
            if (!item) return;

            const stat = props.data.templateStats[item.taskId];
            if (!stat) return '0';

            return (
              <IPText>
                <HrefLink onClick={() => navigateToTemplateUsage(item.taskId)}>
                  {t('translation:General.List.ItemCount', { count: stat.count })}
                </HrefLink>
              </IPText>
            );
          },
        },
        {
          key: 'last-used',
          name: t('tasks:TabTemplates.Columns.LastUsed'),
          minWidth: 150,
          isResizable: true,
          isCollapsible: true,
          onRender: (item?: Task, index?: number, column?: IColumn) => {
            if (!item) return;

            const stat = props.data.templateStats[item.taskId];
            if (!stat) return null;

            return <IPText>{toLocaleDateShort(stat.lastUsed)}</IPText>;
          },
        },
      ];

      if (hasUserFeature(appContext, FeatureTypes.RBAC)) {
        allColumns.splice(3, 0, {
          key: 'permissions',
          name: t('adminAuth:AuthSchemaPicker.Label'),
          minWidth: 100,
          maxWidth: 350,
          isResizable: true,
          isCollapsible: true,
          onRender: (item?: Task, index?: number, column?: IColumn) => {
            if (!item) return null;
            if (!item.authSchemaId) {
              return <IPText>{t('adminAuth:AuthSchemaPicker.Placeholder')}</IPText>;
            } else {
              const schema = appContext.globalDataCache.authSchemas.get(item.authSchemaId);

              return <IPText>{schema.name ?? t('adminAuth:AuthSchemaPicker.Placeholder')}</IPText>;
            }
          },
        });
      }

      return allColumns;
    };

    const newColumns = getColumns();
    const storedSort = getLocalStorageData(appContext, LocalStorageKeys.TaskTemplateListSort)?.split(',');
    const storedColumn =
      storedSort?.length === 2 && (storedSort[1] === '0' || storedSort[1] === '1')
        ? newColumns.find((column) => column.key === storedSort[0])
        : undefined;
    const sortColumn = storedColumn ?? newColumns.find((column) => column.isSorted);
    const isSortedDescending = storedColumn ? storedSort?.[1] === '1' : sortColumn?.isSortedDescending;

    if (sortColumn) {
      newColumns.forEach((column) => {
        column.isSorted = column === sortColumn;
        column.isSortedDescending = column === sortColumn ? isSortedDescending : true;
      });
      const sortedTasks = sortByKey(sortColumn.key, isSortedDescending);
      if (sortedTasks.some((task, index) => task !== props.data.tasks[index])) {
        props.updateTasks(sortedTasks);
      }
    }

    setColumns(newColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const sortByKey = (key: string, isSortedDescending?: boolean): Task[] => {
    let compare: ((a: Task, b: Task) => number) | undefined;
    if (key === 'name') {
      compare = (a: Task, b: Task) => {
        return a.name.localeCompare(b.name);
      };
    } else if (key === 'description') {
      compare = (a: Task, b: Task) => {
        if (!a.description && !b.description) return 0;
        if (!a.description) return -1;
        if (!b.description) return 1;

        return a.description.localeCompare(b.description);
      };
    } else if (key === 'created-by') {
      compare = (a: Task, b: Task) => {
        if (!a.creator && !b.creator) return 0;
        if (!a.creator) return -1;
        if (!b.creator) return 1;

        return a.creator.name.localeCompare(b.creator.name);
      };
    } else if (key === 'used-count') {
      compare = (a: Task, b: Task) => {
        const aStat = props.data.templateStats[a.taskId];
        const bStat = props.data.templateStats[b.taskId];

        if (!aStat && !bStat) return 0;
        if (!aStat) return -1;
        if (!bStat) return 1;

        return aStat.count - bStat.count;
      };
    } else if (key === 'last-used') {
      compare = (a: Task, b: Task) => {
        const aStat = props.data.templateStats[a.taskId];
        const bStat = props.data.templateStats[b.taskId];
        if (!aStat && !bStat) return 0;
        if (!aStat) return -1;
        if (!bStat) return 1;

        return aStat.lastUsed.getTime() - bStat.lastUsed.getTime();
      };
    } else if (key === 'active') {
      compare = (a: Task, b: Task) => {
        const aActive = a.completed === undefined ? 1 : 0;
        const bActive = b.completed === undefined ? 1 : 0;

        return aActive - bActive;
      };
    } else if (key === 'type') {
      compare = (a: Task, b: Task) => {
        return sortOnString(a.getTypeText(t), b.getTypeText(t));
      };
    } else if (key === 'permissions') {
      compare = (a: Task, b: Task) => {
        const a1 = appContext.globalDataCache.authSchemas.get(a.authSchemaId);
        const b1 = appContext.globalDataCache.authSchemas.get(b.authSchemaId);

        return sortOnString(a1.name, b1.name);
      };
    }

    if (!compare) return [...props.data.tasks];

    //the effect above sorts on every render, so sorting a sorted list must change nothing: descending negates the
    //comparison instead of reversing the result, and the stable sort keeps templates that compare equal in their order
    const byKey = compare;
    const direction = isSortedDescending ? -1 : 1;

    return [...props.data.tasks].sort((a: Task, b: Task) => direction * byKey(a, b));
  };

  const sortOnHeaderClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
    if (!column || !columns) return;

    const currColumn = columns.find((_) => _.key === column.key);

    if (!currColumn) return;

    const newColumns: IColumn[] = columns.slice();

    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });

    const sortValue = [currColumn.key, currColumn.isSortedDescending ? '1' : '0'];
    setLocalStorageData(appContext, LocalStorageKeys.TaskTemplateListSort, sortValue.join(','));
    setColumns(newColumns);
    props.updateTasks(sortByKey(currColumn.key, currColumn.isSortedDescending));
  };

  const onRenderRow: IDetailsListProps['onRenderRow'] = (rowProps) => {
    if (rowProps) {
      return (
        <span
          onDoubleClick={() => {
            props.onEdit(rowProps.item);
          }}
        >
          <DetailsRow {...rowProps} />
        </span>
      );
    }

    return null;
  };

  const navigateToTemplateUsage = (templateId: number) => {
    const periodObj: IPeriodFilterData = {
      option: PeriodFilterDataOption.All,
      auditYear: 0,
    };

    const periodParams = getPeriodURLParams(periodObj);

    navigateToExternalUrl(
      `/tasks/alltasks?view=list&templateId=${templateId}&${periodParams}`,
      appContext.user.tenant,
      appContext.user.login.tenantId,
    );
  };

  const getFilteredTasks = (): Task[] => {
    if (!props.searchQuery) return props.data.tasks;

    return props.data.tasks.filter((item) => {
      if (item.name?.toLowerCase().includes(props.searchQuery ?? '')) return true;
      if (item.description?.toLowerCase().includes(props.searchQuery ?? '')) return true;
      if (item.creator?.name?.toLowerCase().includes(props.searchQuery ?? '')) return true;
      if (
        item
          .getTypeText(t)
          .toLowerCase()
          .includes(props.searchQuery ?? '')
      )
        return true;

      if (item.taskTypeId) {
        //the template's own version names the form; the heads cache misses a hidden or forked form
        const formName = item.formVersion?.name ?? appContext.globalDataCache.taskTypes.get(item.taskTypeId).name;
        if (formName.toLowerCase().includes(props.searchQuery ?? '')) return true;
      }

      const tags = appContext.globalDataCache.tags.getItemsForId(item.tagIds);

      return tags.some((tag) =>
        tag
          .value()
          .toLowerCase()
          .includes(props.searchQuery ?? ''),
      );
    });
  };

  const filteredTasks = getFilteredTasks();

  //
  // Main render
  //
  return (
    <IPStack styles={{ root: { position: 'relative' } }} verticalFill>
      <IPStack.Item>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPShimmeredDetailsList
            enableShimmer={props.isBusy}
            onColumnHeaderClick={sortOnHeaderClick}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            columns={columns}
            items={filteredTasks}
            // keyed on the template, so the search filter and the sort keep the selection
            getKey={(item: Task) => String(item.taskId)}
            selection={props.selection}
            selectionMode={props.selection ? IPSelectionMode.single : IPSelectionMode.none}
            layoutMode={IPDetailsListLayoutMode.justified}
            onRenderRow={onRenderRow}
          />
        </IPScrollablePane>
      </IPStack.Item>
      {!props.isBusy && filteredTasks.length === 0 && (
        <IPStack.Item align="center">
          <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
